/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery } from '@tanstack/react-query';
import { PropertiesService } from '@services/requests/housien-services/PropertiesService';
import type { PropertyResponseType } from '@services/housien/types';

/**
 * Builds the one-line property description the lease header shows.
 *
 * The lease read returns only `property_id`, so the header would otherwise
 * be a UUID. Falls back gracefully: headline, then street address, then
 * neighbourhood/city, and finally nothing -- the caller shows the id.
 *
 * @param property - The property as the API returned it.
 * @returns The display line, or an empty string when there is nothing to say.
 */
export function propertyLabel(property: PropertyResponseType | undefined): string {
  if (!property) return '';

  const location = property.location ?? {};
  const street = [location.street, location.number].filter(Boolean).join(', ');
  const area = [location.neighborhood, location.city].filter(Boolean).join(' · ');

  return [property.description?.headline, street, area]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' · ');
}

export interface LeasePropertyResult {
  label: string;
  isLoading: boolean;
}

/**
 * Loads the leased property just for its address line.
 *
 * @param agencyId - The owning agency.
 * @param propertyId - The property the lease points at.
 * @returns The formatted label and its loading flag.
 */
export function useLeaseProperty(agencyId: string, propertyId: string): LeasePropertyResult {
  const query = useQuery({
    queryKey: ['leases', agencyId, 'property', propertyId],
    queryFn: () => PropertiesService.getProperty(agencyId, propertyId),
    enabled: Boolean(agencyId && propertyId),
  });

  return { label: propertyLabel(query.data), isLoading: query.isLoading };
}
